import { Button, Checkbox, FormControlLabel, Grid, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material"
import { useState, useContext, useEffect, useCallback, useMemo } from 'react'
import { Affiliation, AffiliationDomain, AffiliationProgram, Form } from "./AffilliationSearch"
import { LookupTablesContext } from "../../context/LookupTablesContext"
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle'
import axios from "axios"
import { useNavigate, useParams } from "react-router-dom"
import SaveIcon from '@mui/icons-material/Save'
import { toast } from "react-toastify"
import { toastError, toastSuccess } from "../assets/customToasts"
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'

export type AffiliationFormProps = {
	setForm?: React.Dispatch<React.SetStateAction<Form>>
	refreshTable?: () => void
}

export default function AffiliationForm({ setForm, refreshTable }: AffiliationFormProps) {
	// General
	const [disableSave, setDisableSave] = useState<boolean>(false)
	// Form
	const [info, setInfo] = useState<Affiliation>({
		name: '',
		tag: '',
		address: '',
		fk_id_state: null,
		fk_id_country: null,
		phone: '',
		email: '',
		fk_id_affiliation_type: null,
		is_active: true,
		billing_email: '',
		tax_payer_number: '',
		blocked_domains: [],
		programs: []
	})
	// Domain management
	const [currentDomain, setCurrentDomain] = useState<string>('')
	const [deletedDomains, setDeletedDomains] = useState<number[]>([])
	// Program management
	const [currentProgram, setCurrentProgram] = useState<number | null>(null)
	const [deletedPrograms, setDeletedPrograms] = useState<number[]>([])
	// Hooks
	const { id } = useParams()
	const { lookupTables, refreshLookupTables } = useContext(LookupTablesContext)
	const navigate = useNavigate()
	// useMemos
	const isFormSubmittable = useMemo(() => {
		if (!Boolean(info.name) || !Boolean(info.tag) || !Boolean(info.fk_id_country) || !Boolean(info.fk_id_affiliation_type)) return false

		if (info.programs.length === 0) return false

		return true
	}, [info])

	const getAffiliation = useCallback(async () => {
		try {
			const { data } = await axios({
				method: 'GET',
				url: `${process.env.REACT_APP_SIS_BACKEND_URL}/affiliations/${id}`,
				headers: {
					Authorization: `Bearer ${process.env.REACT_APP_SIS_BACKEND_TOKEN}`
				}
			})

			console.log(data)
			setInfo({
				...data,
				blocked_domains: data.blocked_domains || [],
				programs: data.affiliation_programs?.map((p: any) => ({ id: p.id, fk_id_program: p.fk_id_program })) || []
			})
		} catch (err) {
			console.log(err)
			toast.error('Could not fetch affiliation.')
		}
	}, [setInfo])

	const addDomain = useCallback(() => {
		if (!currentDomain) return
		setInfo({
			...info,
			blocked_domains: [...info.blocked_domains, { domain: currentDomain.trim() }]
		})
		setCurrentDomain('')
	}, [currentDomain, info])

	const addProgram = useCallback(() => {
		if (!currentProgram) return
		setInfo({
			...info,
			programs: [...info.programs, { fk_id_program: currentProgram }]
		})
		setCurrentProgram(null)
	}, [currentProgram, info])

	const removeDomain = useCallback((domainToRemove: AffiliationDomain) => {
		// Remove from domains array
		setInfo({
			...info,
			blocked_domains: info.blocked_domains?.filter(d => d.domain !== domainToRemove.domain)
		})

		// if domain has an ID (exists in database), track it for deletion
		if (domainToRemove.id) {
			setDeletedDomains([...deletedDomains, domainToRemove.id])
		}

	}, [info, deletedDomains])

	const removeProgram = useCallback((programToRemove: AffiliationProgram) => {
		// Remove from programs array
		setInfo({
			...info,
			programs: info.programs?.filter(p => p.fk_id_program !== programToRemove.fk_id_program)
		})

		// if program has an ID (exists in database), track it for deletion
		if (programToRemove.id) {
			setDeletedPrograms([...deletedPrograms, programToRemove.id])
		}

	}, [info, deletedPrograms])

	useEffect(() => {
		if (id) {
			getAffiliation()
		}
	}, [id, getAffiliation])

	const onUpdate = useCallback(async () => {
		setDisableSave(true)
		const _toast = toast('Updating affiliation', { toastId: 'toast-affiliations-update', isLoading: true })
		const { blocked_domains, ...affiliation } = info
		console.log('checking data', {
			...affiliation
		})

		try {
			await axios({
				method: 'PUT',
				url: `${process.env.REACT_APP_SIS_BACKEND_URL}/affiliations/${id}`,
				headers: {
					Authorization: `Bearer ${process.env.REACT_APP_SIS_BACKEND_TOKEN}`
				},
				data: {
					...info,
					programs: info.programs.filter((_) => !_.id),
					blocked_domains: info.blocked_domains.filter((_) => !_.id),
					deletedPrograms: deletedPrograms,
					deletedDomains: deletedDomains
				}

			})

			toastSuccess(_toast, 'Affiliation updated.')

			refreshLookupTables(['affiliation'])

			navigate('/affiliations')


		} catch (error) {
			console.log(error)
			toastError(_toast, 'Could not update affiliation.')
		} finally {
			setDisableSave(false)
		}
	}, [info, deletedPrograms, deletedDomains, refreshLookupTables, navigate])

	const onAdd = useCallback(async () => {
		setDisableSave(true)
		const _toast = toast('Creating affiliation', { toastId: 'toast-affiliation-on-add', isLoading: true })

		try {
			const { data } = await axios({
				method: 'POST',
				url: `${process.env.REACT_APP_SIS_BACKEND_URL}/affiliations`,
				headers: {
					Authorization: `Bearer ${process.env.REACT_APP_SIS_BACKEND_TOKEN}`
				},
				data: {
					name: info.name,
					tag: info.tag,
					address: info.address,
					fk_id_state: info.fk_id_state,
					fk_id_country: info.fk_id_country,
					phone: info.phone,
					email: info.email,
					fk_id_affiliation_type: info.fk_id_affiliation_type,
					is_active: info.is_active,
					billing_email: Boolean(info.billing_email) ? info.billing_email : null,
					tax_payer_number: Boolean(info.tax_payer_number) ? info.tax_payer_number : null,
					programs: info.programs.filter((_) => !_.id) || [],
					blocked_domains: info.blocked_domains.filter((_) => !_.id) || []
				}
			})

			await axios({
				method: 'POST',
				url: `${process.env.REACT_APP_DIPLOMAT_URL}/sub-accounts`,
				headers: {
					Authorization: `Bearer ${process.env.REACT_APP_DIPLOMAT_TOKEN}`
				},
				data: {
					id: data.insert.id,
					name: data.insert.name
				}
			})

			toastSuccess(_toast, 'New affiliation created.')

			refreshLookupTables(['affiliation'])

			if (setForm) setForm({ show: false })

			if (refreshTable) refreshTable()

		} catch (err) {
			console.log(err)
			toastError(_toast, 'Could not create new affiliation.')
		} finally {
			setDisableSave(false)
		}
	}, [info, setForm, refreshTable, refreshLookupTables])
	console.log('Affiliation form info:', info)
	return <Grid container maxWidth={'md'} marginY={1} marginX={'auto'}>
		<Grid item xs={12}>
			<TextField id='form-field-name' label='Name' value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} />
		</Grid>

		<Grid item xs={12}>
			<TextField id='form-field-tag' label='TAG' value={info.tag} onChange={(e) => setInfo({ ...info, tag: e.target.value })} />
		</Grid>

		<Grid item xs={12}>
			<TextField id='form-field-phone' label='Phone' value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} />
		</Grid>

		<Grid item xs={12}>
			<TextField id='form-field-academic-email' label='Academic Email' value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} />
		</Grid>

		<Grid item xs={12}>
			<TextField id='form-field-address' label='Address' value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} />
		</Grid>

		<Grid item xs={12}>
			<TextField id='form-field-country' label='Country' value={info.fk_id_country || ''} onChange={(e) => setInfo({ ...info, fk_id_country: Number(e.target.value) })} select>
				{
					lookupTables.country.map((_country, counter) => (
						<MenuItem key={counter} id={`country-option-${_country.id}`} value={_country.id}>{_country.option}</MenuItem>
					))
				}
			</TextField>
		</Grid>

		<Grid item xs={12}>
			<TextField disabled={info.fk_id_country ? false : true} id='form-field-state' label='State' value={info.fk_id_state || ''} onChange={(e) => setInfo({ ...info, fk_id_state: Number(e.target.value) })} select>
				{
					lookupTables.state
						.filter((_state) => _state.fk_id_country === info.fk_id_country)
						.map((_state, index) => <MenuItem key={index} id={`state-option-${_state.id}`} value={_state.id}>{_state.option}</MenuItem>)
				}
			</TextField>
		</Grid>

		<Grid item xs={12}>
			<TextField id='form-field-affiliation-type' label='Affiliation Type' value={info.fk_id_affiliation_type || ''} onChange={(e) => setInfo({ ...info, fk_id_affiliation_type: Number(e.target.value) })} select>
				{
					lookupTables.affiliation_type.map((_affiliationType, index) => {
						return <MenuItem key={index} id={`affiliation-type-option-${_affiliationType.id}`} value={_affiliationType.id}>{_affiliationType.option}</MenuItem>
					})
				}
			</TextField>
		</Grid>

		<Grid item xs={12}>
			<FormControlLabel
				control={
					<Checkbox
						id='form-field-is-active'
						checked={info.is_active}
						onChange={(e) => setInfo({ ...info, is_active: e.target.checked })}
					/>}
				label={'Active'}
			/>
		</Grid>

		{/* Programs Section */}
		<Grid item xs={12} >
			<Typography variant="h5" marginTop={2}>Affiliation Programs:</Typography>
		</Grid>

		<Grid item xs={9.5}>
			<TextField
				id='form-field-program'
				label='Program'
				value={currentProgram || ''}
				onChange={(e) => setCurrentProgram(Number(e.target.value))}
				select
			>
				{
					lookupTables.program
						.filter((_program) => !info.programs?.some(p => p.fk_id_program === _program.id))
						.map((_program, index) => (
							<MenuItem key={index} id={`program-option-${_program.id}`} value={_program.id}>
								{_program.option}
							</MenuItem>
						))
				}
			</TextField>
		</Grid>

		<Grid item xs={2.5}>
			<Button
				id='button-add-program'
				startIcon={<AddIcon />}
				onClick={addProgram}
				sx={{ height: 50, width: "100%" }}
				disabled={!currentProgram}
			>
				ADD
			</Button>
		</Grid>

		{/* Display existing programs */}
		{info.programs && info.programs.length > 0 && (
			<Grid item xs={12}>
				<Typography variant="body2" color="textSecondary" marginBottom={1}>
					Current Programs:
				</Typography>
				{info.programs.map((program, index) => (
					<Grid container key={index} alignItems="center" marginBottom={1}>
						<Grid item xs={10}>
							<TextField
								fullWidth
								value={lookupTables.program.find(p => p.id === program.fk_id_program)?.option || ''}
								disabled
								size="small"
							/>
						</Grid>
						<Grid item xs={2}>
							<IconButton
								id={`button-delete-program-${index}`}
								onClick={() => removeProgram(program)}
								color="error"
							>
								<DeleteIcon />
							</IconButton>
						</Grid>
					</Grid>
				))}
			</Grid>
		)}

		{/* School Domains Section */}
		<Grid item xs={12} >
			<Typography variant="h5" marginTop={2}>School Emails Domains:</Typography>
		</Grid>

		<Grid item xs={9.5}>
			<Tooltip title="Add school email domains, not emails." placement="top">
				<TextField
					id='form-field-school-domain'
					label='School Domain'
					value={currentDomain}
					onChange={(e) => setCurrentDomain(e.target.value)}
				/>
			</Tooltip>
		</Grid>

		<Grid item xs={2.5}>
			<Button
				id='button-add-domain'
				startIcon={<AddIcon />}
				onClick={addDomain}
				sx={{ height: 50, width: "100%" }}
				disabled={!currentDomain.trim()}
			>
				ADD
			</Button>
		</Grid>

		{/* Display existing domains */}
		{info.blocked_domains && info.blocked_domains.length > 0 && (
			<Grid item xs={12}>
				<Typography variant="body2" color="textSecondary" marginBottom={1}>
					Current Domains:
				</Typography>
				{info.blocked_domains.map((domain, index) => (
					<Grid container key={index} alignItems="center" marginBottom={1}>
						<Grid item xs={10}>
							<TextField
								fullWidth
								value={domain.domain}
								disabled
								size="small"
							/>
						</Grid>
						<Grid item xs={2}>
							<IconButton
								id={`button-delete-domain-${index}`}
								onClick={() => removeDomain(domain)}
								color="error"
							>
								<DeleteIcon />
							</IconButton>
						</Grid>
					</Grid>
				))}
			</Grid>
		)}

		<Grid item xs={12} >
			<Typography variant="h5" >Optional:</Typography>
		</Grid>
		<Grid item xs={12}>
			<TextField id='form-field-billing-email' label='Billing Email' value={info.billing_email || ''} onChange={(e) => setInfo({ ...info, billing_email: e.target.value })} />
		</Grid>

		<Grid item xs={12}>
			<TextField id='form-field-tax-payer-number' label='Tax Payer Number' value={info.tax_payer_number || ''} onChange={(e) => setInfo({ ...info, tax_payer_number: e.target.value })} />
		</Grid>

		<Grid item xs={12}>
			<Grid item xs={3}>
				{
					<Button
						id='form-button-save'
						startIcon={id ? <ChangeCircleIcon /> : <SaveIcon />}
						onClick={id ? onUpdate : onAdd}
						sx={{ height: 56, width: "100%" }}
						disabled={disableSave || !isFormSubmittable}
					>
						{id ? 'Update' : 'Save'}
					</Button>
				}
			</Grid>
		</Grid>
	</Grid>
}